import { getMergedChildIds, sortSetCards } from '../utils/setMerges';

// Pokemon TCG API service - uses relative URLs (works in both dev and prod)
const LOCAL_SERVER = '';

// Shared with the search UI so the client and the server agree on the floor —
// the server's own guard is `q.length < 2` (see /api/search), and the client
// used to require 3, silently swallowing valid 2-character queries.
export const MIN_SEARCH_LENGTH = 2;

class PokemonTCGService {
  static async fetchSets() {
    const response = await fetch(`${LOCAL_SERVER}/api/sets`);
    if (!response.ok) throw new Error(`Server error: ${response.status}`);
    const data = await response.json();
    return data;
  }

  static async fetchSetCards(setId) {
    const response = await fetch(`${LOCAL_SERVER}/api/cards/${setId}`);
    if (!response.ok) throw new Error(`Server error: ${response.status}`);
    return response.json();
  }

  // A set as the UI shows it: the set's own cards plus any merged child sets'
  // (BUG-010 — 30th Celebration + Classic Collection), parent cards first.
  // Raw per-set fetches (prices, card hydration) keep using fetchSetCards.
  // A failed child fetch degrades to the parent's cards alone rather than
  // breaking the whole set.
  static async fetchMergedSetCards(setId) {
    const childIds = getMergedChildIds(setId);
    if (childIds.length === 0) return this.fetchSetCards(setId);

    const [parent, ...children] = await Promise.all([
      this.fetchSetCards(setId),
      ...childIds.map((id) =>
        this.fetchSetCards(id).catch((err) => {
          console.error(`Failed to load merged set ${id}:`, err);
          return { data: [] };
        })
      )
    ]);
    const data = sortSetCards([
      ...(parent.data || []),
      ...children.flatMap((c) => c.data || [])
    ]);
    return { ...parent, data, count: data.length, pageSize: data.length, totalCount: data.length };
  }

  static async findSet151() {
    const response = await fetch(`${LOCAL_SERVER}/api/set151`);
    if (!response.ok) throw new Error(`Server error: ${response.status}`);
    const data = await response.json();
    return data.data;
  }

  static async healthCheck() {
    try {
      const response = await fetch(`${LOCAL_SERVER}/api/health`);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  // Global search across every set — one indexed DB query on the server,
  // which returns cards with their full set object attached as `setInfo`
  // (exact-name matches ranked first, then newest set). Replaces the old
  // client-side approach that downloaded the 8 newest sets per search.
  // BUG-008: limit was hardcoded to 6, which silently truncated every search —
  // a Pokemon with 40 prints showed 6 of them and looked like missing data.
  // The server validates and caps at 50 itself, so ask for the cap.
  static async globalSearch(searchTerm, limit = 50) {
    if (searchTerm.length < MIN_SEARCH_LENGTH) return [];

    try {
      const response = await fetch(
        `${LOCAL_SERVER}/api/search?q=${encodeURIComponent(searchTerm)}&limit=${limit}`
      );
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Global search error:', error);
      return [];
    }
  }
}

export default PokemonTCGService;